/**
 * El error de la API, del lado del cliente.
 *
 * La API devuelve siempre un `error` con un código estable, y **el texto que
 * ve la empleada sale del catálogo de i18n a partir de ese código**, nunca del
 * servidor: si el mensaje viniera de la API no habría forma de tenerlo en ES y
 * EN. Esta clase es lo que transporta el código hasta la pantalla.
 */

export class ErrorApi extends Error {
    constructor(
        readonly estado: number,
        readonly codigo: string,
        readonly extra: Readonly<Record<string, unknown>> = {},
        readonly peticionId: string | null = null
    ) {
        super(`${estado} ${codigo}`);
        this.name = 'ErrorApi';
    }

    /**
     * ¿Hay que volver a pedir el PIN o la contraseña?
     *
     * NO todo 401 es una sesión caducada, y darlo por hecho tenía una
     * consecuencia concreta: al mover el alta del segundo factor dentro del
     * panel, teclear un código mal devolvía `401 codigo_invalido` y **echaba
     * a la persona del panel** en lugar de decirle que el código no valía.
     *
     * Así que se decide por el código, que es estable, y el 401 solo cuenta
     * como caducidad cuando el código no dice otra cosa.
     */
    get sesionCaducada(): boolean {
        if (ErrorApi.CODIGOS_401_CON_SIGNIFICADO.includes(this.codigo)) {
            return false;
        }

        return this.codigo === 'sesion_caducada' || this.codigo === 'token_invalido' || this.estado === 401;
    }

    /**
     * 401 que significan algo distinto de "vuelve a entrar".
     *
     * Son respuestas a una credencial que la persona acaba de teclear, no a
     * una sesión que se ha muerto. Tratarlos como caducidad cierra la sesión
     * buena por un error de tecleo.
     */
    private static readonly CODIGOS_401_CON_SIGNIFICADO = [
        'codigo_invalido',
        'codigo_requerido',
        'password_incorrecta',
        'credenciales_invalidas',
        'pin_incorrecto',
        'pin_bloqueado',
        'invitacion_invalida',
        'invitacion_caducada',
        'alta_no_iniciada',
    ];

    /** ¿Este móvil ya no sirve? Hay que volver a la pantalla 00, no al PIN. */
    get dispositivoInutil(): boolean {
        return (
            this.codigo === 'dispositivo_revocado' ||
            this.codigo === 'dispositivo_no_valido' ||
            this.codigo === 'empleada_inactiva'
        );
    }

    /**
     * ¿Merece la pena reintentar?
     *
     * Un 4xx no: el servidor ha entendido y ha dicho no, y reintentar solo
     * gasta batería y datos. Un 5xx o un fallo de red sí. El 429 también, pero
     * con paciencia.
     */
    get reintentable(): boolean {
        return this.estado === 0 || this.estado === 429 || this.estado >= 500;
    }

    get valorExtra(): Readonly<Record<string, unknown>> {
        return this.extra;
    }
}

/** Sin red: no es un error del servidor, y se trata distinto. */
export class ErrorDeRed extends ErrorApi {
    constructor(readonly causa: unknown) {
        super(0, 'sin_red');
        this.name = 'ErrorDeRed';
    }
}
